import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    Activity,
    AlertTriangle,
    CheckCircle,
    ListChecks,
    Megaphone,
    PauseCircle,
    Package,
    Plus,
    RefreshCw,
    Search,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import CampaignDetailsModal from "../components/campaigns/CampaignDetailsModal";
import FloatingActionsMenu from "../components/common/FloatingActionsMenu";
import CustomDropdown from "../components/common/CustomDropdown";
import PageContainer from "../components/layout/PageContainer";
import CampaignEmptyState from "../components/campaigns/CampaignEmptyState";
import CampaignGroup from "../components/campaigns/CampaignGroup";
import CampaignStatCard from "../components/campaigns/CampaignStatCard";
import EditCampaignContextModal from "../components/campaigns/EditCampaignContextModal";
import { ROUTES } from "../config/constants";
import {
    clearUpdateCampaignStatusState,
    fetchCampaignDetails,
    fetchCampaignsBySearch,
    fetchOutreachCampaigns,
    updateCampaignStatus,
} from "../store/outreachSlice";
import { fetchConnections } from "../store/connectionsSlice";
import { fetchSearchHistory } from "../store/leadsSlice";
import {
    getCampaignActionState,
    normalizeCampaignStatus,
} from "../utils/campaignDisplayUtils";
import { hasActiveSenderConnection } from "../utils/connectionGuard";
import { formatSearchHistoryDate } from "../utils/formatDate";
import {
    canCreateCampaignFromSearch,
    getSearchStatusBadge,
} from "../utils/searchStatusUtils";
import useElementInView from "../hooks/useElementInView";

const STATUS_OPTIONS = [
    { value: "all", label: "All Statuses" },
    { value: "active", label: "Active" },
    { value: "completed", label: "Completed" },
    { value: "processing", label: "Processing" },
    { value: "paused", label: "Paused" },
    { value: "failed", label: "Failed" },
];

const UNKNOWN_SEARCH_GROUP_KEY = Symbol("Unknown Search");
const UNKNOWN_SEARCH_LABEL = "Unknown Search";
const CREATE_CAMPAIGN_SEARCH_REQUIRED_MESSAGE =
    "First create a search to create a campaign.";

const MainCampaignsErrorState = ({ error, onRetry }) => (
    <div className="flex min-h-[420px] w-full items-center justify-center rounded-3xl border border-slate-200 bg-gradient-to-br from-white via-rose-50/30 to-indigo-50/30 p-6 shadow-sm">
        <div className="w-full max-w-2xl rounded-2xl border border-rose-100 bg-white/90 p-8 text-center shadow-sm">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50 text-rose-600 ring-1 ring-rose-100">
                <AlertTriangle size={26} aria-hidden="true" />
            </div>

            {error && (
                <p className="mx-auto max-w-lg px-4 py-2 text-sm font-medium text-rose-700">
                    {error}
                </p>
            )}

            <h2 className="mt-5 text-xl font-semibold text-slate-900">
                Unable to load campaigns
            </h2>
            <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-slate-600">
                We couldn&apos;t fetch your campaign data right now. Please check
                your connection and try again.
            </p>

            <button
                type="button"
                onClick={onRetry}
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
            >
                <RefreshCw size={15} aria-hidden="true" />
                Retry
            </button>
        </div>
    </div>
);

const getValidSearchId = (value) => {
    if (value === undefined || value === null) return "";

    const searchId = String(value).trim();
    const normalizedSearchId = searchId.toLowerCase();

    if (
        !searchId ||
        ["unknown", "undefined", "null"].includes(normalizedSearchId)
    ) {
        return "";
    }

    return searchId;
};

const getCampaignSequenceId = (campaign) =>
    String(campaign?.sequence_id || campaign?.id || "").trim();

const getCreatedAtTime = (createdAt) => {
    if (!createdAt) return 0;

    const time = new Date(createdAt).getTime();

    return Number.isNaN(time) ? 0 : time;
};

const matchesStatusFilter = (campaign, statusFilter) => {
    if (statusFilter === "all") return true;

    const normalizedStatus = normalizeCampaignStatus(campaign);

    if (statusFilter === "processing") {
        return ["processing", "preparing"].includes(normalizedStatus);
    }

    return normalizedStatus === statusFilter;
};

const getSearchableText = (campaign) =>
    [
        campaign?.name,
        campaign?.title,
        campaign?.sequence_name,
        campaign?.campaign_name,
        campaign?.product_name,
        campaign?.sequence?.name,
        campaign?.campaign?.name,
    ]
        .filter((value) => value !== null && value !== undefined)
        .map((value) => String(value).trim().toLowerCase())
        .filter(Boolean)
        .join(" ");

const getCampaignStatusUpdateMessage = (response) => {
    if (typeof response === "string" && response.trim()) {
        return response.trim();
    }

    if (typeof response?.message === "string" && response.message.trim()) {
        return response.message.trim();
    }

    return "Campaign status updated successfully.";
};

const getCampaignStatusUpdateError = (error) => {
    if (typeof error === "string" && error.trim()) {
        return error.trim();
    }

    if (typeof error?.message === "string" && error.message.trim()) {
        return error.message.trim();
    }

    return "Failed to update campaign status. Please try again.";
};

const Campaigns = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const topActionsRef = useRef(null);
    const areTopActionsVisible = useElementInView(topActionsRef, {
        rootMargin: "-64px 0px 0px 0px",
        threshold: 0.9,
        minimumVisibleRatio: 0.9,
        topOffset: 64,
    });
    const consumedDashboardCampaignId = useRef("");
    const searchId = getValidSearchId(searchParams.get("searchId"));
    const [campaignSearchTerm, setCampaignSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [detailsModalSequenceId, setDetailsModalSequenceId] = useState(null);
    const [editContextCampaign, setEditContextCampaign] = useState(null);
    const [updatingStatusSequenceId, setUpdatingStatusSequenceId] =
        useState("");
    const [dashboardSelectedCampaignId, setDashboardSelectedCampaignId] =
        useState("");
    const [
        dashboardCampaignScrollTargetId,
        setDashboardCampaignScrollTargetId,
    ] = useState("");
    const [activeSearchCoverageTab, setActiveSearchCoverageTab] =
        useState("with-campaign");
    const [
        showCreateCampaignSearchMessage,
        setShowCreateCampaignSearchMessage,
    ] = useState(false);
    const [
        hasInitializedCampaignGroupExpansion,
        setHasInitializedCampaignGroupExpansion,
    ] = useState(false);

    const {
        campaigns,
        campaignsBySearch,
        dashboardStatus,
        dashboardError,
        selectedCampaign,
        detailsStatus,
    } = useSelector((state) => state.outreach);
    const { searchHistory, searchHistoryStatus, searchHistoryError } =
        useSelector((state) => state.leads);

    const isFilteredView = Boolean(searchId);
    const sourceCampaigns = useMemo(() => {
        const selectedCampaigns = isFilteredView
            ? campaignsBySearch
            : campaigns;

        return Array.isArray(selectedCampaigns) ? selectedCampaigns : [];
    }, [campaigns, campaignsBySearch, isFilteredView]);
    const selectedCampaignDetailsSequenceId =
        getCampaignSequenceId(selectedCampaign);
    const hasSelectedCampaignDetails =
        detailsStatus === "succeeded" && Boolean(selectedCampaignDetailsSequenceId);
    const matchedFilteredSearch = useMemo(() => {
        if (!searchId) return null;

        return (Array.isArray(searchHistory) ? searchHistory : []).find(
            (search) =>
                search?.is_deleted !== true &&
                getValidSearchId(search?.id) === searchId,
        );
    }, [searchHistory, searchId]);
    const selectedSearchQuery =
        sourceCampaigns[0]?.search_query ||
        matchedFilteredSearch?.query_string ||
        "";

    const loadCampaigns = useCallback(() => {
        if (searchId) {
            dispatch(fetchCampaignsBySearch(searchId));
            return;
        }

        dispatch(fetchOutreachCampaigns());
    }, [dispatch, searchId]);

    useEffect(() => {
        loadCampaigns();
    }, [loadCampaigns]);

    useEffect(() => {
        dispatch(fetchSearchHistory());
    }, [dispatch]);

    useEffect(() => {
        setCampaignSearchTerm("");
        setStatusFilter("all");
        setShowCreateCampaignSearchMessage(false);
    }, [searchId]);

    useEffect(() => {
        if (!location.state?.openCreateCampaignSearches || searchId) return;

        setActiveSearchCoverageTab("without-campaign");
        setShowCreateCampaignSearchMessage(true);
        navigate(`${location.pathname}${location.search}`, {
            replace: true,
            state: null,
        });
    }, [location.pathname, location.search, location.state, navigate, searchId]);

    useEffect(() => {
        const dashboardCampaign = location.state?.selectedCampaign || null;
        const dashboardCampaignId =
            dashboardCampaign?.sequence_id ||
            dashboardCampaign?.id ||
            location.state?.selectedCampaignId ||
            "";

        if (!dashboardCampaignId) return;

        navigate(`${location.pathname}${location.search}`, {
            replace: true,
            state: null,
        });

        if (consumedDashboardCampaignId.current === dashboardCampaignId) {
            return;
        }

        consumedDashboardCampaignId.current = dashboardCampaignId;
        setDashboardSelectedCampaignId(dashboardCampaignId);
        setDashboardCampaignScrollTargetId(dashboardCampaignId);
        setActiveSearchCoverageTab("with-campaign");
        setCampaignSearchTerm("");
        setStatusFilter("all");
    }, [location.pathname, location.search, location.state, navigate]);

    const dashboardStats = useMemo(() => {
        const uniqueSearchIds = new Set();
        const hasUnknownSearchCampaign = sourceCampaigns.some((campaign) => {
            const searchId = getValidSearchId(campaign?.search_id);

            if (searchId) {
                uniqueSearchIds.add(searchId);
                return false;
            }

            return true;
        });
        const uniqueSearchCount =
            uniqueSearchIds.size + (hasUnknownSearchCampaign ? 1 : 0);

        return sourceCampaigns.reduce(
            (stats, campaign) => {
                const normalizedStatus = normalizeCampaignStatus(campaign);

                if (normalizedStatus === "active") {
                    stats.activeCampaigns += 1;
                }

                if (normalizedStatus === "completed") {
                    stats.completedCampaigns += 1;
                }

                if (normalizedStatus === "paused") {
                    stats.pausedCampaigns += 1;
                }

                return stats;
            },
            {
                totalCampaigns: sourceCampaigns.length,
                activeCampaigns: 0,
                completedCampaigns: 0,
                pausedCampaigns: 0,
                uniqueSearchCount,
            },
        );
    }, [sourceCampaigns]);

    const filteredCampaigns = useMemo(() => {
        const searchText = campaignSearchTerm.trim().toLowerCase();

        return sourceCampaigns.filter((campaign) => {
            const matchesSearch =
                !searchText || getSearchableText(campaign).includes(searchText);

            return matchesSearch && matchesStatusFilter(campaign, statusFilter);
        });
    }, [campaignSearchTerm, sourceCampaigns, statusFilter]);
    const hasActiveCampaignFilter =
        campaignSearchTerm.trim().length > 0 || statusFilter !== "all";
    const effectiveSearchCoverageTab = useMemo(() => {
        if (
            !isFilteredView &&
            hasActiveCampaignFilter &&
            filteredCampaigns.length > 0
        ) {
            return "with-campaign";
        }

        return activeSearchCoverageTab;
    }, [
        activeSearchCoverageTab,
        filteredCampaigns.length,
        hasActiveCampaignFilter,
        isFilteredView,
    ]);
    useEffect(() => {
        if (effectiveSearchCoverageTab !== activeSearchCoverageTab) {
            setActiveSearchCoverageTab(effectiveSearchCoverageTab);
        }
    }, [activeSearchCoverageTab, effectiveSearchCoverageTab]);

    const groupedCampaigns = useMemo(() => {
        const groups = new Map();

        filteredCampaigns.forEach((campaign) => {
            const searchId = getValidSearchId(campaign?.search_id);
            const searchQuery =
                typeof campaign?.search_query === "string" &&
                campaign.search_query.trim()
                    ? campaign.search_query.trim()
                    : UNKNOWN_SEARCH_LABEL;
            const searchKey = searchId || UNKNOWN_SEARCH_GROUP_KEY;

            if (!groups.has(searchKey)) {
                groups.set(searchKey, {
                    searchId: searchId || null,
                    searchQuery: searchId ? searchQuery : UNKNOWN_SEARCH_LABEL,
                    campaigns: [],
                });
            }

            groups.get(searchKey).campaigns.push(campaign);
        });

        return Array.from(groups.values())
            .map((group) => ({
                ...group,
                campaigns: [...group.campaigns].sort(
                    (firstCampaign, secondCampaign) =>
                        getCreatedAtTime(secondCampaign?.created_at) -
                        getCreatedAtTime(firstCampaign?.created_at),
                ),
            }))
            .sort(
                (firstGroup, secondGroup) =>
                    getCreatedAtTime(secondGroup.campaigns[0]?.created_at) -
                    getCreatedAtTime(firstGroup.campaigns[0]?.created_at),
            );
    }, [filteredCampaigns]);

    useEffect(() => {
        if (
            hasInitializedCampaignGroupExpansion ||
            hasActiveCampaignFilter ||
            groupedCampaigns.length === 0
        ) {
            return;
        }

        setHasInitializedCampaignGroupExpansion(true);
    }, [
        groupedCampaigns,
        hasActiveCampaignFilter,
        hasInitializedCampaignGroupExpansion,
    ]);

    const searchSummary = useMemo(() => {
        const campaignSearchIds = new Set(
            sourceCampaigns
                .map((campaign) => getValidSearchId(campaign?.search_id))
                .filter(Boolean),
        );
        const historyById = new Map();

        (Array.isArray(searchHistory) ? searchHistory : []).forEach(
            (search) => {
                if (search?.is_deleted === true) return;

                const searchRecordId = getValidSearchId(search?.id);
                const queryString = String(search?.query_string || "").trim();

                if (!searchRecordId || !queryString) return;

                const normalizedSearch = {
                    ...search,
                    id: searchRecordId,
                    query_string: queryString,
                };
                const existingSearch = historyById.get(searchRecordId);

                if (
                    !existingSearch ||
                    getCreatedAtTime(normalizedSearch.created_at) >
                        getCreatedAtTime(existingSearch.created_at)
                ) {
                    historyById.set(searchRecordId, normalizedSearch);
                }
            },
        );

        const allSearches = Array.from(historyById.values())
            .map((search) => ({
                ...search,
                hasCampaigns: campaignSearchIds.has(String(search.id)),
            }))
            .sort(
                (firstSearch, secondSearch) =>
                    getCreatedAtTime(secondSearch.created_at) -
                    getCreatedAtTime(firstSearch.created_at),
            );

        const searchesWithCampaigns = allSearches.filter(
            (search) => search.hasCampaigns,
        );
        const searchesWithoutCampaigns = allSearches.filter(
            (search) => !search.hasCampaigns,
        );

        return {
            allSearches,
            totalWithCampaigns: searchesWithCampaigns.length,
            withCampaigns: searchesWithCampaigns,
            totalWithoutCampaigns: searchesWithoutCampaigns.length,
            withoutCampaigns: searchesWithoutCampaigns,
        };
    }, [searchHistory, sourceCampaigns]);

    const handleRetry = () => {
        loadCampaigns();
    };

    const handleClearFilters = () => {
        setCampaignSearchTerm("");
        setStatusFilter("all");
    };

    const handleSelectSearchCoverageTab = (tab) => {
        setActiveSearchCoverageTab(tab);
        setShowCreateCampaignSearchMessage(false);
    };

    const handleOpenCreateCampaignSearches = () => {
        if (
            !isFilteredView &&
            searchHistoryStatus !== "loading" &&
            searchSummary.allSearches.length === 0
        ) {
            return;
        }

        if (isFilteredView) {
            navigate(ROUTES.CAMPAIGNS, {
                state: { openCreateCampaignSearches: true },
            });
            return;
        }

        setActiveSearchCoverageTab("without-campaign");
        setShowCreateCampaignSearchMessage(true);
    };

    const verifyActiveSenderConnection = async () => {
        try {
            const latestConnections = await dispatch(
                fetchConnections(),
            ).unwrap();

            if (hasActiveSenderConnection(latestConnections)) {
                return true;
            }

            toast.error(
                "Please connect an active email sender before creating a campaign.",
            );
            navigate(ROUTES.CONNECTIONS);
            return false;
        } catch {
            toast.error("Unable to verify sender connection. Please try again.");
            return false;
        }
    };

    const handleCreateCampaignFromDashboardSearch = async (search) => {
        const searchRecordId = getValidSearchId(search?.id);
        const queryString = search?.query_string?.trim();

        if (!searchRecordId) return;

        const canStartCampaign = await verifyActiveSenderConnection();

        if (!canStartCampaign) return;

        navigate(ROUTES.SEARCH, {
            state: {
                startCampaignForHistorySearch: {
                    id: searchRecordId,
                    queryString,
                },
            },
        });
    };

    const handleSelectCampaign = useCallback(
        (campaign) => {
            const sequenceId = getCampaignSequenceId(campaign);

            if (!sequenceId) return;

            if (
                hasSelectedCampaignDetails &&
                selectedCampaignDetailsSequenceId === sequenceId
            ) {
                return;
            }

            dispatch(fetchCampaignDetails(sequenceId));
        },
        [
            dispatch,
            hasSelectedCampaignDetails,
            selectedCampaignDetailsSequenceId,
        ],
    );
    const handleDashboardCampaignScrolled = useCallback(() => {
        setDashboardCampaignScrollTargetId("");
    }, []);

    const handleOpenCampaignDetails = (sequenceId) => {
        if (!sequenceId) return;
        setDetailsModalSequenceId(sequenceId);
    };

    const handleCloseCampaignDetails = () => {
        setDetailsModalSequenceId(null);
    };

    const handleOpenEditCampaignContext = (campaign) => {
        if (!campaign?.sequence_id) return;

        const actionState = getCampaignActionState(campaign);

        if (!actionState.canEditContext) {
            toast.error(
                "Campaign context can only be edited for active or paused campaigns.",
            );
            return;
        }

        setEditContextCampaign(campaign);
    };

    const handleCloseEditCampaignContext = () => {
        setEditContextCampaign(null);
    };

    const handleCampaignContextUpdated = (message, sequenceId) => {
        toast.success(
            message ||
                "Campaign context modified successfully. Remaining agent drafts will reflect these changes.",
        );
        loadCampaigns();

        if (detailsModalSequenceId && detailsModalSequenceId === sequenceId) {
            dispatch(fetchCampaignDetails(sequenceId));
        }
    };

    const handleUpdateCampaignStatus = async (campaign, nextStatus) => {
        const sequenceId = campaign?.sequence_id;
        const actionState = getCampaignActionState(campaign);
        const normalizedNextStatus =
            nextStatus === "paused" || nextStatus === "active"
                ? nextStatus
                : "";

        if (!sequenceId || !normalizedNextStatus) {
            toast.error("This campaign status cannot be changed manually.");
            return;
        }

        const isAllowedAction =
            (normalizedNextStatus === "paused" && actionState.canPause) ||
            (normalizedNextStatus === "active" && actionState.canSetActive);

        if (!isAllowedAction) {
            toast.error("This campaign status cannot be changed manually.");
            return;
        }

        if (updatingStatusSequenceId) return;

        setUpdatingStatusSequenceId(sequenceId);
        dispatch(clearUpdateCampaignStatusState());

        try {
            const response = await dispatch(
                updateCampaignStatus({
                    sequenceId,
                    status: normalizedNextStatus,
                }),
            ).unwrap();

            toast.success(getCampaignStatusUpdateMessage(response));
            loadCampaigns();

            if (detailsModalSequenceId && detailsModalSequenceId === sequenceId) {
                dispatch(fetchCampaignDetails(sequenceId));
            }
        } catch (error) {
            toast.error(getCampaignStatusUpdateError(error));
        } finally {
            setUpdatingStatusSequenceId("");
            dispatch(clearUpdateCampaignStatusState());
        }
    };

    const isCampaignDetailsModalOpen = Boolean(detailsModalSequenceId);
    const isEditContextModalOpen = Boolean(editContextCampaign);
    const detailsModalCampaignSummary = useMemo(() => {
        if (!detailsModalSequenceId) return null;

        const loadedCampaigns = [
            ...(Array.isArray(campaignsBySearch) ? campaignsBySearch : []),
            ...(Array.isArray(campaigns) ? campaigns : []),
        ];

        return (
            loadedCampaigns.find(
                (campaign) =>
                    getCampaignSequenceId(campaign) === detailsModalSequenceId,
            ) || null
        );
    }, [campaigns, campaignsBySearch, detailsModalSequenceId]);
    const totalSearchSummaryCount = searchSummary.totalWithoutCampaigns;
    const searchesWithCampaignCount = searchSummary.totalWithCampaigns;
    const hasSearchHistory = searchSummary.allSearches.length > 0;
    const isCreateCampaignShortcutDisabled =
        !isFilteredView &&
        searchHistoryStatus !== "loading" &&
        !hasSearchHistory;
    const createCampaignShortcutTitle = isCreateCampaignShortcutDisabled
        ? CREATE_CAMPAIGN_SEARCH_REQUIRED_MESSAGE
        : "Create a campaign from an existing search.";
    const pageActions = [
        {
            label: "Search Leads",
            icon: Search,
            to: ROUTES.SEARCH,
        },
        {
            label: "My Product",
            icon: Package,
            to: ROUTES.PRODUCTS,
        },
        {
            label: "Create Campaign",
            icon: Plus,
            onClick: handleOpenCreateCampaignSearches,
            disabled: isCreateCampaignShortcutDisabled,
            title: createCampaignShortcutTitle,
        },
    ];
    const filteredSearchForCampaignCreation = {
        id: searchId,
        query_string: selectedSearchQuery,
    };
    const hasLoadedCampaignContent = sourceCampaigns.length > 0;
    const isInitialCampaignLoading =
        dashboardStatus === "loading" && !hasLoadedCampaignContent;
    const canRenderCampaignContent =
        dashboardStatus === "succeeded" ||
        (dashboardStatus === "loading" && hasLoadedCampaignContent);

    return (
        <main className="min-h-screen bg-[#F8F8FE]">
            <PageContainer className="py-4 pb-20 sm:pb-20">
                <header className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <h1 className="min-w-0 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                            {isFilteredView
                                ? selectedSearchQuery
                                    ? `Campaigns for "${selectedSearchQuery}"`
                                    : "Campaigns for Selected Search"
                                : "My Campaigns"}
                        </h1>
                        <p className="mt-2 max-w-2xl text-sm text-slate-600">
                            {isFilteredView
                                ? "Track campaigns related to this lead search."
                                : "Track, review, and manage outreach campaigns across your searches."}
                        </p>
                    </div>

                    <div
                        ref={topActionsRef}
                        className="grid w-full grid-cols-3 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:justify-end"
                    >
                        <button
                            type="button"
                            onClick={() => navigate(ROUTES.SEARCH)}
                            className="inline-flex min-w-0 w-full items-center justify-center gap-1.5 rounded-xl border border-indigo-100 bg-indigo-50 px-2 py-2.5 text-xs font-semibold text-indigo-700 shadow-sm transition hover:bg-indigo-100 sm:w-auto sm:gap-2 sm:px-4 sm:text-sm"
                        >
                            <Search size={16} aria-hidden="true" />
                            <span className="sm:hidden">Searches</span>
                            <span className="hidden sm:inline">
                                Search Leads
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate(ROUTES.PRODUCTS)}
                            className="inline-flex min-w-0 w-full items-center justify-center gap-1.5 rounded-xl border border-indigo-100 bg-indigo-50 px-2 py-2.5 text-xs font-semibold text-indigo-700 shadow-sm transition hover:bg-indigo-100 sm:w-auto sm:gap-2 sm:px-4 sm:text-sm"
                        >
                            <Package size={16} aria-hidden="true" />
                            <span className="sm:hidden">Products</span>
                            <span className="hidden sm:inline">My Product</span>
                        </button>
                        <button
                            type="button"
                            onClick={handleOpenCreateCampaignSearches}
                            disabled={isCreateCampaignShortcutDisabled}
                            title={createCampaignShortcutTitle}
                            className="inline-flex min-w-0 w-full items-center justify-center gap-1.5 rounded-xl bg-indigo-600 px-2 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:gap-2 sm:px-4 sm:text-sm"
                        >
                            <Plus size={16} aria-hidden="true" />
                            <span className="sm:hidden">Create Campaign</span>
                            <span className="hidden sm:inline">Create Campaign</span>
                        </button>
                    </div>
                </header>

                {isInitialCampaignLoading && (
                    <div className="rounded-xl border border-indigo-100 bg-indigo-50 px-4 py-4 text-sm font-semibold text-indigo-800">
                        Loading campaigns...
                    </div>
                )}

                {dashboardStatus === "failed" && (
                    <MainCampaignsErrorState
                        error={dashboardError}
                        onRetry={handleRetry}
                    />
                )}

                {canRenderCampaignContent &&
                    isFilteredView &&
                    sourceCampaigns.length === 0 && (
                        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-8 text-center shadow-sm">
                            <CampaignEmptyState
                                title="Campaigns do not exist for this search."
                                description={
                                    selectedSearchQuery
                                        ? `Search: ${selectedSearchQuery}`
                                        : "Create a campaign from this search to see it here."
                                }
                            />
                            <button
                                type="button"
                                onClick={() =>
                                    handleCreateCampaignFromDashboardSearch(
                                        filteredSearchForCampaignCreation,
                                    )
                                }
                                disabled={!searchId}
                                className="mt-5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
                            >
                                Create Campaign
                            </button>
                        </div>
                    )}

                {canRenderCampaignContent &&
                    !isFilteredView &&
                    sourceCampaigns.length === 0 &&
                    searchHistoryStatus !== "loading" &&
                    searchHistoryStatus !== "failed" &&
                    totalSearchSummaryCount === 0 && (
                        <CampaignEmptyState
                            title="No campaigns created yet."
                            description="Create your first campaign to start your outreach process."
                        />
                    )}

                {canRenderCampaignContent &&
                    ((isFilteredView && sourceCampaigns.length > 0) ||
                        (!isFilteredView &&
                            (sourceCampaigns.length > 0 ||
                                searchHistoryStatus === "loading" ||
                                searchHistoryStatus === "failed" ||
                                totalSearchSummaryCount > 0))) && (
                        <div>
                            <div className="flex flex-col gap-4 sm:gap-5">
                                <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                                    <CampaignStatCard
                                        label="Total Campaigns"
                                        value={dashboardStats.totalCampaigns}
                                        icon={ListChecks}
                                        variant="blue"
                                    />
                                    <CampaignStatCard
                                        label="Active Campaigns"
                                        value={dashboardStats.activeCampaigns}
                                        icon={Activity}
                                        variant="green"
                                    />
                                    <CampaignStatCard
                                        label="Completed Campaigns"
                                        value={
                                            dashboardStats.completedCampaigns
                                        }
                                        icon={CheckCircle}
                                        variant="blue"
                                    />
                                    <CampaignStatCard
                                        label="Paused Campaigns"
                                        value={dashboardStats.pausedCampaigns}
                                        icon={PauseCircle}
                                        variant="gray"
                                    />
                                </section>
                                <div
                                    className="flex flex-col gap-3 sm:gap-5 xl:grid xl:grid-cols-[460px_minmax(0,1fr)] 2xl:grid-cols-[460px_minmax(0,1fr)] xl:items-center xl:gap-3 xl:px-4"
                                >
                                    {isFilteredView ? (
                                        <div className="min-w-0">
                                            <p className="text-sm font-semibold text-slate-900">
                                                Campaigns for this search
                                            </p>
                                            {selectedSearchQuery && (
                                                <p className="mt-1 text-sm text-slate-500">
                                                    Search:{" "}
                                                    {selectedSearchQuery}
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="w-full min-w-0 xl:-ml-4 xl:w-[calc(100%_+_1rem)]">
                                            <div
                                                role="tablist"
                                                aria-label="Search campaign coverage"
                                                className="grid h-11 min-h-11 w-full min-w-0 grid-cols-2 gap-1 rounded-lg border border-slate-200 bg-white p-1 shadow-sm"
                                            >
                                                <button
                                                    type="button"
                                                    role="tab"
                                                    aria-selected={
                                                        effectiveSearchCoverageTab ===
                                                        "with-campaign"
                                                    }
                                                    onClick={() =>
                                                        handleSelectSearchCoverageTab(
                                                            "with-campaign",
                                                        )
                                                    }
                                                    className={`flex h-full min-w-0 items-center justify-center rounded-lg px-2 text-center text-[10px] font-semibold leading-3 transition-colors sm:px-3.5 sm:text-sm sm:leading-5 ${
                                                        effectiveSearchCoverageTab ===
                                                        "with-campaign"
                                                            ? "bg-indigo-100 text-indigo-700 shadow-sm"
                                                            : "text-slate-600 hover:bg-indigo-50 hover:text-indigo-700"
                                                    }`}
                                                >
                                                    <span className="whitespace-normal sm:whitespace-nowrap">
                                                        {`Searches With Campaign (${searchesWithCampaignCount})`}
                                                    </span>
                                                </button>
                                                <button
                                                    type="button"
                                                    role="tab"
                                                    aria-selected={
                                                        effectiveSearchCoverageTab ===
                                                        "without-campaign"
                                                    }
                                                    onClick={() =>
                                                        handleSelectSearchCoverageTab(
                                                            "without-campaign",
                                                        )
                                                    }
                                                    className={`flex h-full min-w-0 items-center justify-center rounded-lg px-2 text-center text-[10px] font-semibold leading-3 transition-colors sm:px-3.5 sm:text-sm sm:leading-5 ${
                                                        effectiveSearchCoverageTab ===
                                                        "without-campaign"
                                                            ? "bg-indigo-100 text-indigo-700 shadow-sm"
                                                            : "text-slate-600 hover:bg-indigo-50 hover:text-indigo-700"
                                                    }`}
                                                >
                                                    <span className="whitespace-normal sm:whitespace-nowrap">
                                                        {`Searches Without Campaign (${totalSearchSummaryCount})`}
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    <div className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_10rem] gap-3 lg:w-full lg:shrink-0">
                                        <label
                                            htmlFor="campaign-search-filter"
                                            className="sr-only"
                                        >
                                            Search campaigns
                                        </label>
                                        <input
                                            id="campaign-search-filter"
                                            type="search"
                                            autoFocus
                                            value={campaignSearchTerm}
                                            onChange={(event) =>
                                                setCampaignSearchTerm(
                                                    event.target.value,
                                                )
                                            }
                                            placeholder="Search campaigns..."
                                            className="h-11 w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100"
                                        />

                                        <CustomDropdown
                                            id="campaign-status-filter"
                                            value={statusFilter}
                                            options={STATUS_OPTIONS}
                                            onChange={setStatusFilter}
                                            ariaLabel="Filter campaigns by status"
                                            className="w-full min-w-0"
                                            buttonClassName="h-11"
                                        />
                                    </div>
                                </div>
                                {isFilteredView &&
                                    (filteredCampaigns.length === 0 ? (
                                        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-8 text-center shadow-sm">
                                            <p className="font-semibold text-slate-900">
                                                No campaigns match your filters.
                                            </p>
                                            <button
                                                type="button"
                                                onClick={handleClearFilters}
                                                className="mt-4 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                                            >
                                                Clear Filters
                                            </button>
                                        </div>
                                    ) : (
                                        <section className="translate-y-0 overflow-hidden rounded-2xl border border-slate-200 bg-white opacity-100 shadow-sm transition-all duration-200 ease-out">
                                            <h2 className="px-4 py-3 text-base font-semibold text-slate-900">
                                                Campaigns for this search
                                            </h2>
                                            {groupedCampaigns.map((group, index) => (
                                                <CampaignGroup
                                                    key={
                                                        group.searchId ||
                                                        group.searchQuery
                                                    }
                                                    searchQuery={
                                                        group.searchQuery
                                                    }
                                                    campaigns={group.campaigns}
                                                    selectedCampaignDetails={
                                                        selectedCampaign
                                                    }
                                                    selectedSequenceId={
                                                        dashboardSelectedCampaignId
                                                    }
                                                    scrollTargetSequenceId={
                                                        dashboardCampaignScrollTargetId
                                                    }
                                                    onScrollTargetResolved={
                                                        handleDashboardCampaignScrolled
                                                    }
                                                    onEditCampaignContext={
                                                        handleOpenEditCampaignContext
                                                    }
                                                    onUpdateCampaignStatus={
                                                        handleUpdateCampaignStatus
                                                    }
                                                    onViewCampaign={
                                                        handleOpenCampaignDetails
                                                    }
                                                    onSelectCampaign={
                                                        handleSelectCampaign
                                                    }
                                                    updatingStatusSequenceId={
                                                        updatingStatusSequenceId
                                                    }
                                                    initiallyExpanded={
                                                        index === 0 &&
                                                        !hasInitializedCampaignGroupExpansion &&
                                                        !hasActiveCampaignFilter
                                                    }
                                                    autoExpand={hasActiveCampaignFilter}
                                                    variant="row"
                                                />
                                            ))}
                                        </section>
                                    ))}

                                {!isFilteredView &&
                                    effectiveSearchCoverageTab ===
                                        "with-campaign" &&
                                    sourceCampaigns.length === 0 && (
                                        <section className="flex min-h-[18rem] items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-10 text-center shadow-sm">
                                            <div className="max-w-sm">
                                                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
                                                    <Megaphone
                                                        size={22}
                                                        aria-hidden="true"
                                                    />
                                                </div>
                                                <h2 className="mt-4 text-lg font-semibold text-slate-900">
                                                    No campaigns created yet.
                                                </h2>
                                                <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
                                                    Create your first campaign
                                                    to start your outreach
                                                    process.
                                                </p>
                                            </div>
                                        </section>
                                    )}

                                {!isFilteredView &&
                                    effectiveSearchCoverageTab ===
                                        "with-campaign" &&
                                    sourceCampaigns.length > 0 &&
                                    (filteredCampaigns.length === 0 ? (
                                        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-8 text-center shadow-sm">
                                            <p className="font-semibold text-slate-900">
                                                No campaigns match your filters.
                                            </p>
                                            <button
                                                type="button"
                                                onClick={handleClearFilters}
                                                className="mt-4 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                                            >
                                                Clear Filters
                                            </button>
                                        </div>
                                    ) : (
                                        <section className="translate-y-0 overflow-hidden rounded-2xl border border-slate-200 bg-white opacity-100 shadow-sm transition-all duration-200 ease-out">
                                            {groupedCampaigns.map((group, index) => (
                                                <CampaignGroup
                                                    key={
                                                        group.searchId ||
                                                        group.searchQuery
                                                    }
                                                    searchQuery={
                                                        group.searchQuery
                                                    }
                                                    campaigns={group.campaigns}
                                                    selectedCampaignDetails={
                                                        selectedCampaign
                                                    }
                                                    selectedSequenceId={
                                                        dashboardSelectedCampaignId
                                                    }
                                                    scrollTargetSequenceId={
                                                        dashboardCampaignScrollTargetId
                                                    }
                                                    onScrollTargetResolved={
                                                        handleDashboardCampaignScrolled
                                                    }
                                                    onEditCampaignContext={
                                                        handleOpenEditCampaignContext
                                                    }
                                                    onUpdateCampaignStatus={
                                                        handleUpdateCampaignStatus
                                                    }
                                                    onViewCampaign={
                                                        handleOpenCampaignDetails
                                                    }
                                                    onSelectCampaign={
                                                        handleSelectCampaign
                                                    }
                                                    updatingStatusSequenceId={
                                                        updatingStatusSequenceId
                                                    }
                                                    initiallyExpanded={
                                                        index === 0 &&
                                                        !hasInitializedCampaignGroupExpansion &&
                                                        !hasActiveCampaignFilter
                                                    }
                                                    autoExpand={hasActiveCampaignFilter}
                                                    variant="row"
                                                />
                                            ))}
                                        </section>
                                    ))}

                                {!isFilteredView &&
                                    effectiveSearchCoverageTab ===
                                    "without-campaign" && (
                                        <section className="translate-y-0 overflow-hidden rounded-2xl border border-slate-200 bg-white opacity-100 shadow-sm transition-all duration-200 ease-out">
                                            {showCreateCampaignSearchMessage && (
                                                <div className="m-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-700">
                                                    Create a campaign on
                                                    searches below.
                                                </div>
                                            )}

                                            {searchHistoryStatus ===
                                                "loading" && (
                                                <div className="m-4 rounded-lg border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm font-semibold text-indigo-700">
                                                    Loading search history...
                                                </div>
                                            )}

                                            {searchHistoryStatus ===
                                                "failed" && (
                                                <div className="m-4 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                                                    <p className="font-semibold">
                                                        Unable to load search
                                                        history.
                                                    </p>
                                                    {searchHistoryError && (
                                                        <p className="mt-1">
                                                            {searchHistoryError}
                                                        </p>
                                                    )}
                                                </div>
                                            )}

                                            {searchHistoryStatus !==
                                                "loading" &&
                                                searchHistoryStatus !==
                                                    "failed" &&
                                                totalSearchSummaryCount ===
                                                    0 && (
                                                    <div className="m-4 rounded-lg border border-slate-100 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                                                        {searchSummary
                                                            .allSearches
                                                            .length > 0
                                                            ? "All searched terms already have campaigns."
                                                            : "No search history found yet."}
                                                    </div>
                                                )}

                                            {searchHistoryStatus !==
                                                "loading" &&
                                                searchHistoryStatus !==
                                                    "failed" &&
                                                totalSearchSummaryCount > 0 && (
                                                    <div className="product-scrollbar max-h-[520px] overflow-y-auto">
                                                        {searchSummary.withoutCampaigns.map(
                                                            (search) => {
                                                                const statusBadge =
                                                                    getSearchStatusBadge(
                                                                        search.status,
                                                                    );
                                                                const canCreateCampaign =
                                                                    canCreateCampaignFromSearch(
                                                                        search.status,
                                                                    ) &&
                                                                    Boolean(
                                                                        search.id,
                                                                    );

                                                                return (
                                                                    <div
                                                                        key={
                                                                            search.id
                                                                        }
                                                                        className="border-b border-slate-200 px-4 py-3 transition-colors odd:bg-slate-50 even:bg-white hover:bg-amber-50/50"
                                                                    >
                                                                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                                                            <div className="min-w-0">
                                                                                <div className="flex items-start justify-between gap-3">
                                                                                    <p className="truncate text-base font-semibold text-slate-900">
                                                                                        <span className="font-medium text-slate-500">
                                                                                            Your
                                                                                            search:{" "}
                                                                                        </span>
                                                                                        {
                                                                                            search.query_string
                                                                                        }
                                                                                    </p>
                                                                                    {statusBadge.label ? (
                                                                                        <span
                                                                                            className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${statusBadge.className}`}
                                                                                        >
                                                                                            {
                                                                                                statusBadge.label
                                                                                            }
                                                                                        </span>
                                                                                    ) : null}
                                                                                </div>
                                                                                <p className="mt-1 text-xs text-slate-500">
                                                                                    Created:{" "}
                                                                                    {formatSearchHistoryDate(
                                                                                        search.created_at,
                                                                                    )}
                                                                                </p>
                                                                            </div>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() =>
                                                                                    canCreateCampaign &&
                                                                                    handleCreateCampaignFromDashboardSearch(
                                                                                        search,
                                                                                    )
                                                                                }
                                                                                disabled={
                                                                                    !canCreateCampaign
                                                                                }
                                                                                title={
                                                                                    canCreateCampaign
                                                                                        ? undefined
                                                                                        : "Search must be completed before creating a campaign."
                                                                                }
                                                                                className="w-fit shrink-0 whitespace-nowrap rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
                                                                            >
                                                                                Create
                                                                                Campaign
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            },
                                                        )}
                                                    </div>
                                                )}
                                        </section>
                                    )}
                            </div>
                        </div>
                    )}
            </PageContainer>
            <FloatingActionsMenu
                actions={pageActions}
                visible={!areTopActionsVisible}
            />

            <CampaignDetailsModal
                isOpen={isCampaignDetailsModalOpen}
                onClose={handleCloseCampaignDetails}
                sequenceId={detailsModalSequenceId}
                campaignSummary={detailsModalCampaignSummary}
            />
            <EditCampaignContextModal
                isOpen={isEditContextModalOpen}
                campaign={editContextCampaign}
                onClose={handleCloseEditCampaignContext}
                onUpdated={handleCampaignContextUpdated}
            />
        </main>
    );
};

export default Campaigns;
